import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { getSettings } from '../../../models/settingsModel';

function AssetDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSettings();
    fetchAssetDetails();
  }, [id]);

  const loadSettings = async () => {
    try {
      const settingsResponse = await getSettings();
      
      if (settingsResponse && settingsResponse.status === 1 && settingsResponse.data) {
        setSettings(settingsResponse.data);
      } else {
        setSettings(null);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Don't show error to user, just continue without settings
    }
  };

  const fetchAssetDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Make API call to get asset details
      const response = await api.get(`/api/assets/details/${id}`);
      
      if (response.data && response.data.status === 1) {
        setAsset(response.data.data);
      } else {
        setError(response.data?.message || 'Asset not found');
        toast.error(response.data?.message || 'Asset not found');
      }
    } catch (err) {
      console.error('Error fetching asset details:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load asset details. The asset may not exist or the QR code may be invalid.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00' || dateString === '0') {
      return 'N/A';
    }
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading asset details...</p>
        </div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white shadow-lg rounded-lg p-8 text-center">
            <div className="text-center mb-6">
              {settings?.site_logo_preview && settings.site_logo_preview !== 'Not available' ? (
                <img 
                  src={settings.site_logo_preview} 
                  alt="Logo" 
                  className="mx-auto h-16 w-auto max-w-[200px] object-contain mb-4"
                />
              ) : (
                <img 
                  src="/portal/assets/images/Silulumanzi.jpg" 
                  alt="Silulumanzi Logo" 
                  className="mx-auto h-16 mb-4"
                />
              )}
            </div>
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Asset Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Header with Logo */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-white">Asset Details</h1>
                <p className="text-blue-100 text-sm mt-0.5">Tag: {asset.asset_tag}</p>
              </div>
              {settings?.site_logo_preview && settings.site_logo_preview !== 'Not available' ? (
                <div className="bg-white rounded px-3 py-2">
                  <img 
                    src={settings.site_logo_preview} 
                    alt="Logo" 
                    className="h-10 w-auto max-w-[200px] object-contain"
                  />
                </div>
              ) : (
                <div className="bg-white rounded px-3 py-2">
                  <img 
                    src="/portal/assets/images/Silulumanzi.jpg" 
                    alt="Silulumanzi Logo" 
                    className="h-10 w-auto object-contain"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Asset Information */}
          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50 w-2/5">
                      Asset Name/Model
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {asset.asset_name || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50">
                      Vendor
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {asset.vendor || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50">
                      Asset Tag
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {asset.asset_tag || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50">
                      Asset Serial Number
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {asset.asset_serial || 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50">
                      Date of Purchase
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {formatDate(asset.asset_date_purchased)}
                    </td>
                  </tr>
                  
                  {asset.asset_description && asset.asset_description.trim() !== '' && (
                    <tr>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50 align-top">
                        Description
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {asset.asset_description}
                      </td>
                    </tr>
                  )}
                  
                  {asset.asset_processor && asset.asset_processor.trim() !== '' && (
                    <tr>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50">
                        Asset Processor
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {asset.processor || asset.asset_processor}
                      </td>
                    </tr>
                  )}
                  
                  {asset.asset_storage && asset.asset_storage.trim() !== '' && (
                    <tr>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50">
                        Storage/Measurement
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {asset.storage || asset.asset_storage}
                      </td>
                    </tr>
                  )}
                  
                  {asset.asset_ram && asset.asset_ram.trim() !== '' && (
                    <tr>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50">
                        RAM
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {asset.ram || asset.asset_ram}
                      </td>
                    </tr>
                  )}
                  
                  <tr>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50">
                      Current User
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700">
                      {asset.user ? asset.user.user_full_name : 'Not Allocated'}
                    </td>
                  </tr>
                  
                  {asset.state && (
                    <tr>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 bg-gray-50">
                        Condition
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          asset.state.toLowerCase() === 'new' ? 'bg-green-100 text-green-800' :
                          asset.state.toLowerCase() === 'good' ? 'bg-blue-100 text-blue-800' :
                          asset.state.toLowerCase() === 'fair' ? 'bg-yellow-100 text-yellow-800' :
                          asset.state.toLowerCase() === 'retired' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {asset.state}
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Activity History */}
            {asset.history && asset.history.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity History</h3>
                <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
                  {asset.history.map((activity, index) => (
                    <div key={index} className="mb-3 last:mb-0">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-gray-900">{activity.report_description}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(activity.report_date)} by {activity.user_full_name || 'System'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer Actions */}
            <div className="mt-8 flex gap-4">
              <button
                onClick={() => window.print()}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Print Details
              </button>
              <button
                onClick={() => navigate('/dashboard/assets/hardware?page=1&pageSize=20')}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Go to Portal
              </button>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>For any issues with this asset, please contact IT Support</p>
        </div>
      </div>
    </div>
  );
}

export default AssetDetails;
